import React, { useEffect, useState } from 'react'
import InfoIcon from '@mui/icons-material/Info'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  CircularProgress,
  FormControl,
  FormGroup,
  Input,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  Tooltip
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import FaberFeatureToggle from 'common/FaberComponents/FaberFeatureToggle'
import format from 'date-fns/format'
import { FormikContextType, useFormikContext } from 'formik'
import { changeMonth } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { GetSubscriptionData } from 'types/api/faber/subscription.type'

import { FormDetailsProps } from './RefundFormV2Dialog'
export function RefundFormV2({
  customerEmail,
  isRefundLoading,
  handleRefundLoading,
  customerId,
  mutateRefundOtpack,
  transactionType
}: RefundFormProps) {
  const formik: FormikContextType<FormDetailsProps> = useFormikContext()
  const [endDate] = useState<Date>(changeMonth(new Date(), +3))
  const [subscription, setSubscription] = useState<
    GetSubscriptionData | undefined
  >()
  const [showCancelOption, setShowCancelOption] = useState('no')
  const faberToken = OrbitSession.get('faberToken')
  //check if user has an active subscription or not
  const { data: getAllSubscriptions, refetch: refetchSubscriptionsReq } =
    useQuery(['getAllSubscriptions', [customerId, endDate]], async () => {
      return SubscriptionsAPI.getAllSubscriptions(
        faberToken,
        10,
        0,
        [],
        [],
        'Customer ID',
        customerId,
        'DESC',
        '2022-10-20',
        format(endDate, 'yyyy-MM-dd')
      )
    })

  useEffect(() => {
    refetchSubscriptionsReq()
    formik.validateForm()
  }, [])

  useEffect(() => {
    if (
      getAllSubscriptions?.data.body.data &&
      getAllSubscriptions.data.body.data.length > 0
    ) {
      setSubscription(getAllSubscriptions.data.body.data[0])
    }
  }, [getAllSubscriptions])

  const handleSubmit = () => {
    if (transactionType.includes('ONETIMEPACK')) {
      mutateRefundOtpack({
        faberToken,
        refundPaymentID: formik.values.refundPaymentID,
        refundReason: formik.values.refundReason,
        ticketId: formik.values.ticketId,
        userId: customerId
      })
    } else {
      formik.handleSubmit()
    }
  }

  return (
    <Stack
      sx={{
        p: 2,
        boxShadow: 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px',
        borderRadius: '8px'
      }}
    >
      <Box sx={{ py: 2 }}>
        <FormControl disabled variant='standard' sx={{ width: '90%' }}>
          <InputLabel>Customer Email</InputLabel>
          <Input
            fullWidth
            multiline
            sx={{ mt: 2 }}
            id='component-disabled'
            defaultValue={customerEmail}
          />
        </FormControl>
      </Box>
      <Box sx={{ py: 2 }}>
        <FormControl variant='standard' sx={{ width: '90%' }}>
          <InputLabel shrink htmlFor='bootstrap-input'>
            Zendesk Ticket ID*
          </InputLabel>
          <TextField
            name='ticketId'
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
            }}
            value={formik.values.ticketId}
            autoFocus
            label=''
            id='name'
            type='text'
            fullWidth
            variant='standard'
            required
            sx={{ mt: 2 }}
            error={formik.touched.ticketId && Boolean(formik.errors.ticketId)}
            helperText={formik.touched.ticketId && formik.errors.ticketId}
          />
        </FormControl>
      </Box>

      <Box sx={{ py: 2 }}>
        <InputLabel shrink htmlFor='bootstrap-input'>
          Refund Reasons*
        </InputLabel>
        <FormControl variant='standard' sx={{ width: '90%' }}>
          <TextField
            name='refundReason'
            value={formik.values.refundReason}
            multiline
            autoFocus
            required
            onChange={(e) => {
              formik.handleBlur(e)
              formik.handleChange(e)
            }}
            error={
              formik.touched.refundReason && Boolean(formik.errors.refundReason)
            }
            helperText={
              formik.touched.refundReason && formik.errors.refundReason
            }
          />
        </FormControl>
        {!transactionType.includes('ONETIMEPACK') ? (
          <FaberFeatureToggle
            feature='cancel-sub-refund'
            activeComponent={
              <Box sx={{ py: 4 }}>
                <FormGroup aria-label='position' row>
                  <Stack flexDirection={'row'} justifyContent='left'>
                    <InputLabel shrink>
                      Do you want to cancel the customer&apos;s subscription
                      immediately?
                      <Tooltip
                        sx={{ px: 0.5 }}
                        PopperProps={{
                          sx: { maxWidth: '170px', textAlign: 'center' }
                        }}
                        title={
                          'Cancellation is effective immediately. Customer will no longer be able to use data and other benefits. This action cannot be undone.'
                        }
                      >
                        <InfoIcon fontSize='small' />
                      </Tooltip>
                    </InputLabel>
                  </Stack>
                  <TextField
                    name='cancelSub'
                    value={showCancelOption}
                    select
                    onChange={(e) => {
                      setShowCancelOption(e.target.value)
                    }}
                    disabled={
                      subscription && subscription.status === 'CANCELLED'
                    }
                  >
                    <MenuItem
                      value={'no'}
                      onClick={() => {
                        formik.setFieldValue('terminateSub', false)
                      }}
                    >
                      No
                    </MenuItem>
                    <MenuItem
                      value='yes'
                      onClick={() => {
                        formik.setFieldValue('terminateSub', true)
                      }}
                    >
                      Yes
                    </MenuItem>
                  </TextField>
                </FormGroup>
              </Box>
            }
          />
        ) : null}
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, mt: 2 }}>
          <LoadingButton
            loading={isRefundLoading}
            loadingIndicator={
              <CircularProgress size={24} sx={{ color: 'white' }} />
            }
            variant='contained'
            sx={{
              '&.Mui-disabled': {
                border: 'none'
              }
            }}
            disabled={!formik.isValid}
            type='submit'
            onClick={() => {
              handleRefundLoading()
              handleSubmit()
            }}
          >
            Submit
          </LoadingButton>
        </Box>
      </Box>
    </Stack>
  )
}

interface RefundFormProps {
  customerEmail: string
  isRefundLoading: boolean
  handleRefundLoading: () => void
  customerId: string
  totalOtpackDataUsed: number
  transactionType: string
  mutateRefundOtpack: ({
    faberToken,
    refundPaymentID,
    refundReason,
    userId,
    ticketId
  }) => void
}
